/**
 * A disposal stack for the short-lived browser resources an export pass needs:
 * an AudioContext, an object URL, a detached <video>, a MediaRecorder.
 *
 * Cleaning these up inline at the end of a long `try` block means every early
 * `return` and every thrown error skips the cleanup. That matters most for
 * AudioContext: browsers cap a page at a handful of live contexts, so a few
 * failed exports are enough to make `new AudioContext()` throw and break
 * exporting for the rest of the session.
 */
export interface ResourceScope {
  /** Registers a teardown callback; returns `value` so it can wrap an expression. */
  add<T>(value: T, dispose: (value: T) => void): T;
  /** Registers a bare teardown callback. */
  defer(dispose: () => void): void;
  /** Runs every teardown in reverse order. Safe to call more than once. */
  dispose(): void;
  readonly disposed: boolean;
  readonly size: number;
}

export const createResourceScope = (
  onDisposeError: (error: unknown) => void = (error) =>
    console.warn("Resource cleanup failed:", error),
): ResourceScope => {
  const teardowns: Array<() => void> = [];
  let disposed = false;

  return {
    add<T>(value: T, dispose: (value: T) => void): T {
      teardowns.push(() => dispose(value));
      return value;
    },
    defer(dispose: () => void) {
      teardowns.push(dispose);
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      // Reverse order: later resources usually depend on earlier ones.
      while (teardowns.length > 0) {
        const teardown = teardowns.pop()!;
        try {
          teardown();
        } catch (error) {
          // One failing teardown must not strand the rest.
          onDisposeError(error);
        }
      }
    },
    get disposed() {
      return disposed;
    },
    get size() {
      return teardowns.length;
    },
  };
};
